//CoolEarthList.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import MainLayout from '../../layouts/MainLayout';

import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';

const CoolEarthList = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [sugos, setSugos] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedSugoId, setSelectedSugoId] = useState(null);

  const fetchSugos = useCallback(() => {
    const config = {
      url: "/coolearth/myallsugo",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id}
    };
    // console.log("My Sugo Search Payload: ", config);
    fetchData(config, { showSuccessToast: false }).then(data => setSugos(data.sugos));
  }, [authState.token, fetchData, authState.user._id]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchSugos();
  }, [authState.isLoggedIn, fetchSugos]);

  const handleDelete = (id) => {
    const config = {
      url: `/coolearth/${id}`,
      method: "delete",
      headers: { Authorization: authState.token }
    };
    fetchData(config).then(() => fetchSugos());
  }

  const handleView = (sugoId) => {
    setSelectedSugoId(prevId => (prevId === sugoId ? null : sugoId));
  };

  const handleCloseSugokDetails = () => {
    setSelectedSugoId(null);
  };

  const handleSugoClick = (sugoId) => {
    setSelectedSugoId(sugoId);
  };

  const handleSugoShare = (sugoId) => {
    setSelectedSugoId(sugoId);
  };

  const handleSugoSave = (sugoId) => {
    setSelectedSugoId(sugoId);
  };

  const handleSugoPrint = (sugoId) => {
    setSelectedSugoId(sugoId);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  const sortedSugos = [...sugos].sort((a, b) => new Date(b.targetStartDate) - new Date(a.targetStartDate));

  return (
    <>
    <MainLayout>
      <div className='mt-12'>
        <h3 className='text-lg mt-1 mx-8 border-b border-b-gray-300 text-white'>My Sustainable Goal List</h3>
      </div>
      <div className="my-2 mx-auto max-w-[700px] py-4">
        <div className="border border-gray-300 p-4 mt-4">
          <div className="mt-2">
            <div className="mt-2">
              {sugos && sugos.length !== 0 && (
                <div className="flex justify-between items-center border-b border-b-gray-300">
                  <div>
                  <h3 className='text-m mt-1 mx-1 text-yellow-200'>{sugos.length} Sustainable Goals Found!</h3>
                  </div>
                  <div className='mr-4'>
                  <Link to='/sugos/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                    <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Add New
                  </Link>
                </div>
              </div>
              )}
            </div>
          </div>
          {loading ? (
            <Loader />
          ) : (
            <div>
              {sugos && sugos.length === 0 ? (
                <div className='w-[600px] h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                  <div className="flex justify-between items-center">
                  <div>
                  <h3 className='text-lg mt-1 mx-2 text-orange-400'>No Sustainable Goal Record Found!</h3>
                  </div>
                  <div>
                  <Link to='/sugos/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                    <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Add New
                  </Link>
                </div>
              </div>
                </div>
              ) : (
                sortedSugos.map((sugo, index) => (
                  <div key={sugo._id} className='bg-white my-1 p-1 text-gray-600 rounded-md shadow-md text-sm'>
                    <div className='flex'>
                      <span className='font-medium'>{index + 1}{'. '}{sugo.title}{', '}({formatDateTime(sugo.dateCreated)})</span>
                      <Tooltip text={"View details"} position={"top"}>
                        <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(sugo._id)}>
                          <i className="fa-solid fa-eye"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Edit this record"} position={"top"}>
                        <Link to={`/sugos/${sugo._id}`} className='mr-4 text-green-600 cursor-pointer'>
                          <i className="fa-solid fa-pen"></i>
                        </Link>
                      </Tooltip>
                      <Tooltip text={"Delete this record"} position={"top"}>
                        <span className='text-red-500 cursor-pointer mr-4' onClick={() => handleDelete(sugo._id)}>
                          <i className="fa-solid fa-trash"></i>
                        </span>
                      </Tooltip>
                    </div>
                    {selectedSugoId === sugo._id && (
                      <div className='bg-gray-200 text-black'>
                        <p className='bg-blue-700 text-white justify-center text-m mt-2 mb-2'>
                          <h2 className='text-white'><strong>SUGO (Sustainable Goals) Details</strong></h2>
                        </p>
                        <p className='text-yellow-600'><strong>Sustainable Goal Code:</strong> {sugo.sugoCode}</p>

                        <p><strong>Title:</strong> {sugo.title}</p>
                        <p><strong>Description:</strong> {sugo.description}</p>
                        <p><strong>Category:</strong> {sugo.sugoCategory}</p>
                                            
                        <p><strong>Target Start Date:</strong> {formatDateTime(sugo.targetStartDate)}</p>
                        <p><strong>Target End Date:</strong> {formatDateTime(sugo.targetEndDate)}</p>
                        <p><strong>Date Created:</strong> {formatDateTime(sugo.dateCreated)}</p>
                        <p><strong>Remarks:</strong> {sugo.remarks}</p>
                        <Tooltip text={"Share"} position={"top"}>
                        <span className='text-green-500 cursor-pointer mr-4' onClick={() => handleSugoShare(sugo._id)}>
                          <i className="fa-solid fa-share"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Save"} position={"top"}>
                        <span className='text-blue-500 cursor-pointer mr-4' onClick={() => handleSugoSave(sugo._id)}>
                          <i className="fa-solid fa-save"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Print"} position={"top"}>
                        <span className='text-orange-500 cursor-pointer mr-4' onClick={() => handleSugoPrint(sugo._id)}>
                          <i className="fa-solid fa-print"></i>
                        </span>
                      </Tooltip>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
    </>
  )
}

export default CoolEarthList;
